import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';

const KubernetesAppDetailsPage = () => {
    const { clusterId, namespace, name } = useParams();
    const [app, setApp] = useState<any>(null);
    const [daprComponents, setDaprComponents] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchAppDetails = async () => {
            try {
                // Fetch application details
                const appsResponse = await axios.get(`/api/monitoring/clusters/${clusterId}/apps?namespace=${namespace}`);
                const appData = appsResponse.data.data.find((a:any) =>
                    a.name === name && a.namespace === namespace
                );
                setApp(appData);

                // If app has Dapr, fetch related components
                if (appData && appData.hasDapr) {
                    const componentsResponse = await axios.get(`/api/monitoring/clusters/${clusterId}/dapr-components`);
                    setDaprComponents(componentsResponse.data.data);
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching app details:', error);
                setLoading(false);
            }
        };

        fetchAppDetails();
    }, [clusterId, namespace, name]);

    const openLogs = (containerName:any) => {
        const url = `/kubernetes/logs?cluster=${clusterId}&namespace=${namespace}&pod=${name}${containerName ? `&container=${containerName}` : ''}`;
        window.open(url, '_blank');
    };

    if (loading) {
        return <div className="loading-spinner">Loading...</div>;
    }

    if (!app) {
        return <div>Application not found</div>;
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex items-center mb-6">
                <Link
                    to={`/kubernetes/clusters/${clusterId}`}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                >
                    ? Back to Cluster
                </Link>
                <h1 className="text-2xl font-bold">{app.name}</h1>
                <span className={`ml-4 px-3 py-1 text-sm font-semibold rounded-full ${app.status === 'Available'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : app.status === 'Progressing'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                    {app.status}
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* App Details */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                    <h2 className="text-lg font-medium mb-4">Application Details</h2>

                    <div className="space-y-4">
                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-gray-500 dark:text-gray-400">Name</div>
                            <div className="col-span-2">{app.name}</div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-gray-500 dark:text-gray-400">Namespace</div>
                            <div className="col-span-2">{app.namespace}</div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-gray-500 dark:text-gray-400">Cluster</div>
                            <div className="col-span-2">{clusterId}</div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-gray-500 dark:text-gray-400">Type</div>
                            <div className="col-span-2">{app.type}</div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-gray-500 dark:text-gray-400">Status</div>
                            <div className="col-span-2">{app.status}</div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-gray-500 dark:text-gray-400">Replicas</div>
                            <div className="col-span-2">{app.replicas}</div>
                        </div>

                        <div className="grid grid-cols-3 gap-4">
                            <div className="col-span-1 text-gray-500 dark:text-gray-400">Created</div>
                            <div className="col-span-2">{new Date(app.createdAt).toLocaleString()}</div>
                        </div>
                    </div>

                    <div className="mt-6 flex space-x-4">
                        <button
                            onClick={() => openLogs(app.name)}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                            View Application Logs
                        </button>
                    </div>
                </div>

                {/* Dapr Details */}
                {app.hasDapr && (
                    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
                        <h2 className="text-lg font-medium mb-4">Dapr Configuration</h2>

                        <div className="space-y-4">
                            <div className="grid grid-cols-3 gap-4">
                                <div className="col-span-1 text-gray-500 dark:text-gray-400">Dapr Enabled</div>
                                <div className="col-span-2">Yes</div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div className="col-span-1 text-gray-500 dark:text-gray-400">App ID</div>
                                <div className="col-span-2">{app.daprAppId || 'Not specified'}</div>
                            </div>

                            <div className="grid grid-cols-3 gap-4">
                                <div className="col-span-1 text-gray-500 dark:text-gray-400">Sidecar</div>
                                <div className="col-span-2">Running</div>
                            </div>
                        </div>

                        <div className="mt-6 flex space-x-4">
                            <button
                                onClick={() => openLogs('daprd')}
                                className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                            >
                                View Dapr Sidecar Logs
                            </button>
                        </div>

                        {daprComponents.length > 0 && (
                            <div className="mt-6">
                                <h3 className="text-md font-medium mb-3">Available Dapr Components</h3>
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead className="bg-gray-100 dark:bg-gray-800">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                                            {daprComponents.map((component) => (
                                                <tr key={component.name} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                                    <td className="px-4 py-2 whitespace-nowrap">{component.name}</td>
                                                    <td className="px-4 py-2 whitespace-nowrap">{component.type}</td>
                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${component.status === 'Healthy'
                                                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                                : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                                            }`}>
                                                            {component.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Other details like events, metrics, etc. can be added here */}
        </div>
    );
};

export default KubernetesAppDetailsPage;